import {
    HttpLink,
    InMemoryCache,
    ApolloLink,
    ApolloClient,
    makeVar,
} from '@apollo/client';
import { LocalState } from '@apollo/client/local-state';
import { getMainDefinition } from '@apollo/client/utilities';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { createClient } from 'graphql-ws';
import { ErrorLink } from '@apollo/client/link/error';
import { SetContextLink } from '@apollo/client/link/context';
import { CombinedGraphQLErrors } from '@apollo/client/errors';
import { matchPath } from 'react-router';

import { ENV_GRAPHQL_URL, ENV_GRAPHQL_WS_URL } from 'env';
import { getLoginUrl } from 'common/utils/getUrl';
import { getCookie } from 'common/utils/cookie';
import { EGraphQLError } from 'common/types';
import { CSRF_COOKIE_NAMES, CSRF_HEADER_NAME } from 'common/constants/csrf';
import { PUBLIC_ROUTES } from 'route/constants/routes';

import generatedIntrospection from './introspectionResult';

// Flag to suppress auth redirect during logging out
export const isLoggingOutVar = makeVar(false);

const httpLink = new HttpLink({
    uri: ENV_GRAPHQL_URL,
    credentials: 'include',
});

// The session cookie alone is not enough for a mutation: the back-end pairs it
// with a CSRF token. Read per operation, since logging in rotates the token.
const csrfLink = new SetContextLink(({ headers }) => {
    const token = CSRF_COOKIE_NAMES.map(getCookie).find(Boolean);

    if (!token) return {};

    return { headers: { ...headers, [CSRF_HEADER_NAME]: token } };
});

const wsLink = new GraphQLWsLink(
    createClient({
        url: ENV_GRAPHQL_WS_URL,
        lazyCloseTimeout: 30000,
    })
);

// Add error link to catch AuthNotLoggedIn and redirect to login
const errorLink = new ErrorLink(({ error, operation }) => {
    // Only handle errors for queries and mutations, not subscriptions
    const mainDef = getMainDefinition(operation.query);
    const isSubscription =
        mainDef.kind === 'OperationDefinition' &&
        mainDef.operation === 'subscription';

    if (isSubscription) return;

    if (CombinedGraphQLErrors.is(error)) {
        error.errors.some(({ extensions }) => {
            const errorCode = extensions?.code;

            if (errorCode === EGraphQLError.AuthNotLoggedIn) {
                if (isLoggingOutVar()) return true;

                const currentPath = window.location.pathname;

                // Check if current path is a public route and skip redirect if it is
                const isPublicRoute = PUBLIC_ROUTES.some((routePattern) =>
                    matchPath({ path: routePattern }, currentPath)
                );

                if (!isPublicRoute) {
                    window.location.href = getLoginUrl();
                }

                return true;
            }

            return false;
        });
    }
});

// Combines the http and ws link into one link and uses one
// or the other according to the type of operation being executed.
const splitLink = ApolloLink.split(
    ({ query }) => {
        const definition = getMainDefinition(query);

        return (
            definition.kind === 'OperationDefinition' &&
            definition.operation === 'subscription'
        );
    },
    wsLink,
    httpLink
);

// Combine errorLink, csrfLink and splitLink
const combinedLink = ApolloLink.from([errorLink, csrfLink, splitLink]);

export const apolloClient = new ApolloClient({
    link: combinedLink,
    localState: new LocalState(),
    cache: new InMemoryCache({
        possibleTypes: generatedIntrospection.possibleTypes,
        typePolicies: {
            LibraryCollection: {
                fields: {
                    access: {
                        // Don't merge access field on update, always use incoming version
                        merge: false,
                    },
                },
            },
            PortfolioItemGroup: {
                fields: {
                    portfolioItems: {
                        keyArgs: false,
                    },
                },
            },
            DevelopmentItemGroup: {
                fields: {
                    developmentItems: {
                        merge: false,
                    },
                },
            },
            DevelopmentItem: {
                fields: {
                    files: {
                        merge: false,
                    },
                },
            },
            BudgetRequest: {
                fields: {
                    files: {
                        // Don't merge files on update, always use incoming version
                        merge: false,
                    },
                },
            },
            PortfolioItem: {
                fields: {
                    files: {
                        // Don't merge access field on update, always use incoming version
                        merge: false,
                    },
                    scoring: {
                        merge: false,
                    },
                },
            },
            PortfolioItemListItem: {
                // Embed in the parent rather than normalizing by id: the same
                // logical row can appear under multiple parent skills with
                // different metadata (e.g. `isMandatory`), so a normalized
                // entry would let one parent's write clobber another's view.
                keyFields: false,
            },
            IndividualAssignment: {
                fields: {
                    files: {
                        // Don't merge access field on update, always use incoming version
                        merge: false,
                    },
                },
            },
            CollectiveAssignment: {
                fields: {
                    files: {
                        // Don't merge access field on update, always use incoming version
                        merge: false,
                    },
                },
            },
            UserAssignment: {
                fields: {
                    files: {
                        // Don't merge access field on update, always use incoming version
                        merge: false,
                    },
                },
            },
            Discussion: {
                fields: {
                    comments: {
                        // Don't merge access field on update, always use incoming version
                        merge: false,
                    },
                },
            },
            Subscription: {
                fields: {
                    updatedComment: {
                        // Don't merge access field on update, always use incoming version
                        merge: false,
                    },
                    newNotification: {
                        // Don't merge access field on update, always use incoming version
                        merge: false,
                    },
                },
            },
            Training: {
                fields: {
                    sharedFiles: {
                        // Don't merge access field on update, always use incoming version
                        merge: false,
                    },
                    conditions: {
                        // Don't merge access field on update, always use incoming version
                        merge: false,
                    },
                },
            },
            LinkModule: {
                fields: {
                    conditions: {
                        // Don't merge field on update, always use incoming version
                        merge: false,
                    },
                },
            },
            Offer: {
                fields: {
                    groupConditions: {
                        merge: false,
                    },
                },
            },
            OfferEvent: {
                fields: {
                    dates: {
                        // Don't merge field on update, always use incoming version
                        merge: false,
                    },
                    groupEnrollmentCounts: {
                        merge: false,
                    },
                },
            },
            File: {
                keyFields: ['url'],
            },
            Plan: {
                keyFields: ['name'],
            },
            Organisation: {
                keyFields: ['slug'],
                fields: {
                    theme: {
                        merge: false,
                    },
                },
            },
            Group: {
                fields: {
                    extraCategoryValues: {
                        merge: false,
                    },
                    users: {
                        merge: false,
                    },
                    managers: {
                        merge: false,
                    },
                    trainings: {
                        merge: false,
                    },
                },
            },
            ExtraCategory: {
                fields: {
                    choiceOptions: {
                        merge: false,
                    },
                },
            },
            ExtraCategoryBooleanValue: {
                keyFields: ['category', ['id'], 'modelId'],
            },
            ExtraCategoryChoiceValue: {
                keyFields: [
                    'category',
                    ['id'],
                    'modelId',
                    'choiceValue',
                    ['id'],
                ],
            },
            ExtraCategoryDatetimeValue: {
                keyFields: ['category', ['id'], 'modelId'],
            },
            ExtraCategoryStringValue: {
                keyFields: ['category', ['id'], ['modelId', 'stringValue']],
            },
            GroupCondition: {
                merge(_existing, incoming) {
                    return incoming;
                },
            },
            ModuleGroup: {
                fields: {
                    conditions: {
                        // Don't merge field on update, always use incoming version
                        merge: false,
                    },
                    fullConditions: {
                        // Don't merge field on update, always use incoming version
                        merge: false,
                    },
                    modules: {
                        // Don't merge field on update, always use incoming version
                        merge: false,
                    },
                },
            },
            ExtraCategorySearch: {
                keyFields: ['id', 'value'],
            },
            Course: {
                fields: {
                    extraCategoryValues: {
                        merge: false,
                    },
                },
            },
            User: {
                fields: {
                    participantGroups: {
                        merge: false,
                    },
                    managerGroups: {
                        merge: false,
                    },
                },
            },
            Skill: {
                fields: {
                    groups: {
                        merge: false,
                    },
                    certificates: {
                        merge: false,
                    },
                },
            },
            Certificate: {
                fields: {
                    scoring: {
                        // Don't merge scoring field on update, always use incoming version
                        merge: false,
                    },
                },
            },
            Query: {
                fields: {
                    // Client-only field with no resolver; default to an empty
                    // list so reads before the first writeQuery resolve.
                    activeDrawers: {
                        read: (existing?: string[]) => existing ?? [],
                    },
                    scoringSystems: {
                        merge: false,
                    },
                    offersSettings: {
                        merge: false,
                    },
                    overviewTrainings: {
                        // Exclude pagination args so pages merge into the same field
                        keyArgs: ['q', 'extraFields', 'role'],
                        merge(existing, incoming, { args }) {
                            // If no after cursor is set we know its a first request
                            // So we then want to start over with building the cache
                            if (!existing || !args?.after) return incoming;

                            const newEdges = [
                                ...existing.edges,
                                ...incoming.edges,
                            ];

                            return {
                                ...existing,
                                ...incoming,
                                edges: newEdges,
                            };
                        },
                    },
                    overviewOfferEvents: {
                        keyArgs: [
                            'q',
                            'extraCategories',
                            'types',
                            'forRoles',
                            'fromDate',
                        ],
                        merge(existing, incoming, { args }) {
                            // If no after cursor is set we know its a first request
                            // So we then want to start over with building the cache
                            if (!existing || !args?.after) return incoming;

                            const newEdges = [
                                ...existing.edges,
                                ...incoming.edges,
                            ];

                            return {
                                ...existing,
                                ...incoming,
                                edges: newEdges,
                            };
                        },
                    },
                    offers: {
                        keyArgs: ['q', 'extraCategories', 'types', 'forRoles'],
                        merge(existing, incoming, { args }) {
                            // If no after cursor is set we know its a first request
                            // So we then want to start over with building the cache
                            if (!existing || !args?.after) return incoming;

                            const newEdges = [
                                ...existing.edges,
                                ...incoming.edges,
                            ];

                            return {
                                ...existing,
                                ...incoming,
                                edges: newEdges,
                            };
                        },
                    },
                    paginatedOfferEvents: {
                        keyArgs: [
                            'q',
                            'extraCategories',
                            'types',
                            'forRoles',
                            'offset',
                            'fromDate',
                            'enrollmentStatus',
                            'certificateId',
                            'ids',
                            'cancelled',
                            'canEnrollParticipants',
                            'enrollableForUser',
                        ],
                        merge(existing, incoming, { args }) {
                            // If no after cursor is set we know its a first request
                            // So we then want to start over with building the cache
                            if (!existing || !args?.after) return incoming;

                            const newEdges = [
                                ...existing.edges,
                                ...incoming.edges,
                            ];

                            return {
                                ...existing,
                                ...incoming,
                                edges: newEdges,
                            };
                        },
                    },
                    moduleGroup: {
                        // This will make sure that when ModuleGroup is already in the cache and we use a different
                        // query it will also be found.
                        read(_, { args, toReference }) {
                            if (!args) return;

                            return toReference({
                                __typename: 'ModuleGroup',
                                id: args.id,
                            });
                        },
                    },
                    portfolio: {
                        merge(existing, incoming) {
                            if (!existing) return incoming;

                            return {
                                ...existing,
                                ...incoming,
                            };
                        },
                    },
                    externalContents: {
                        keyArgs: ['q', 'extraCategories', 'offset'],
                        merge(existing, incoming, { args }) {
                            // If no after cursor is set we know its a first request
                            // So we then want to start over with building the cache
                            if (!existing || !args?.after) return incoming;

                            const newEdges = [
                                ...existing.edges,
                                ...incoming.edges,
                            ];

                            return {
                                ...existing,
                                ...incoming,
                                edges: newEdges,
                            };
                        },
                    },
                    authors: {
                        merge(existing, incoming) {
                            if (!existing) return incoming;

                            return {
                                ...existing,
                                ...incoming,
                            };
                        },
                    },
                    trainingPortfolioReports: {
                        merge: false,
                    },
                    notifications: {
                        keyArgs: false,
                        merge(existing, incoming, { args }) {
                            // If no after cursor is set we know its a first request
                            // So we then want to start over with building the cache
                            if (!existing || !args?.after) return incoming;

                            const existingEdges = existing?.edges || [];

                            // Filter out incoming edges that already exist
                            const newEdges = [
                                ...existingEdges,
                                ...(incoming?.edges || []).filter(
                                    (incomingEdge: {
                                        node: { __ref: string };
                                    }) =>
                                        !existingEdges.some(
                                            (existingEdge: {
                                                node: { __ref: string };
                                            }) =>
                                                existingEdge.node.__ref ===
                                                incomingEdge.node.__ref
                                        )
                                ),
                            ];

                            return {
                                ...existing,
                                ...incoming,
                                edges: newEdges,
                            };
                        },
                    },
                    receivedCertificates: {
                        keyArgs: false,
                        merge(existing, incoming, { args }) {
                            // If no after cursor is set we know its a first request
                            // So we then want to start over with building the cache
                            if (!existing || !args?.after) return incoming;

                            const newEdges = [
                                ...existing.edges,
                                ...incoming.edges,
                            ];

                            return {
                                ...existing,
                                ...incoming,
                                edges: newEdges,
                            };
                        },
                    },
                },
            },
        },
    }),
});
